import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { getReadableAppVersion } from 'environments/version-getters';
import { isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { AppInstallService, InstallTarget, InstallTargetKind } from 'services/app-install.service';
import { VersionCheckService } from 'services/version-check.service';
import { TelemetryService } from 'services/telemetry.service';
import { LoggingService } from 'services/logging.service';
import { PopoverController } from 'providers/popover-controller';
import { CsAppInstallSheet } from 'components/cs-app-install-sheet/cs-app-install-sheet';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { getAllowedPageDuringWelcome } from 'app/app-page-helpers';
import { DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { WelcomePhase } from '@cf-shared/data-model/fitness-plan';
import { CoachBranding, CoachRelationship } from '@cf-shared/data-model/coach';
import { describeError } from '@cf-shared/error-describe';
import { SettingsService } from 'services/settings.service';
import { ReviewPromptService } from 'services/review-prompt.service';
import { IOS_WRITE_REVIEW_URL, androidWriteReviewUrlForEnv } from '@cf-shared/data-model/app-install-urls';
import { AppEnvironment } from '../../environments/environment';
import { openExternalLink } from 'util/open-external-link';
import { openConsentPreferencesEvent, PubSubService } from 'services/pubsub.service';
import { getMyApp } from 'app/app-interface';
import { waitForAngularUpdate } from 'services/browser-utils';
import { sleep } from '@cf-shared/shared-util';
import { IProgramSettingsPage, ProgramSettingsTab } from 'pages/program-settings-page/program-settings-interface';
import { IEditWorkoutsPage } from 'pages/edit-workouts/edit-workouts-interface';
import { IEquipmentSearchPage } from 'pages/equipment/equipment-search-interface';
import { IMealPlanPage } from 'pages/meal-plan/meal-plan-interface';
import { IFoodsPage } from 'pages/foods/foods-interface';
import { IAppSettingsPage } from 'pages/settings/app-settings-interface';
import { INotificationSettingsPage } from 'pages/notification-settings/notification-settings-interface';
import { IUserProfilePage } from 'pages/user-profile/user-profile-interface';
import { IHelpPage } from 'pages/help-page/help-page-interface';
import { ISendFeedbackPage } from 'pages/send-feedback/send-feedback-interface';
import { IChangelogPage } from 'pages/changelog/changelog-interface';
import { IProgressPage } from 'pages/progress/progress-interface';
import { IAdminRootPage } from 'pages/admin-root/admin-root-interface';
import { IReferralHubPage } from 'pages/referral-hub/referral-hub-interface';
import { ICoachSettingsPage } from 'pages/coach-settings/coach-settings-interface';
import { ISendCoachMessagePage } from 'pages/send-coach-message/send-coach-message-interface';
import { IIntakeFormPage } from 'pages/intake-form/intake-form-interface';
import { SettingsPageParams } from './settings-page-interface';
import {
  SettingsItem,
  SettingsSection,
  isIntakePending as isIntakePendingForColumns,
  isSettingsItemEnabled,
  splitSettingsColumns,
} from './settings-section-access';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ToastController } from 'providers/toast-controller';
import { LoadingController } from 'providers/loading-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';

import { LL } from 'i18n/i18n.service';

@Component({
    selector: 'page-settings',
    templateUrl: 'settings-page.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsPage extends CsPage<SettingsPageParams> {
  protected LL = LL;
  public appVersion: string;
  public settingsSections: SettingsSection[] = [];
  private allowedPagesInWelcome: string[] = [];
  private welcomePhase: WelcomePhase;

  private versionClickedTime = -1;
  private versionClickedCounter = 0;
  private docWatcher: DocWatcher;
  private justSelfDetached = false;
  // resolved on view enter so the button's visibility and tap handler agree
  // about whether we open the install sheet vs fire the PWA prompt vs hide
  private installTarget: InstallTarget = { kind: InstallTargetKind.None };

  constructor(
    public navCtrl: NavController,
    private auth: AuthService,
    private userDb: UserDb,
    private settings: SettingsService,
    private reviewPrompt: ReviewPromptService,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private cloudFunctions: CloudFunctionsService,
    private appInstallSvc: AppInstallService,
    private popoverCtrl: PopoverController,
    private versionCheck: VersionCheckService,
    private telemetry: TelemetryService,
    private logging: LoggingService,
    private loadingCtrl: LoadingController,
    private pubSub: PubSubService,
  ) {
    super();
    this.appVersion = getReadableAppVersion();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
      });
    }

    // get allowed pages and welcome phase status
    this.allowedPagesInWelcome = getAllowedPageDuringWelcome();
    const plan = await this.userDb.loadPlanAsync(this.docWatcher);
    this.welcomePhase = plan?.welcomePhase || WelcomePhase.Intro;

    // resolve install affordance once per view enter; the template guards
    // shouldShowInstallButton() against this cached target rather than
    // re-resolving each CD tick (async-pipe-free)
    this.installTarget = await this.appInstallSvc.resolveInstallTarget();

    this.initializeSettingsSections();
    // OnPush: surface the post-await state (welcomePhase + settingsSections)
    // to the template; without this, the new info-card guard and column-swap
    // may not render until another CD trigger fires
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  async ionViewDidEnter() {
    // handle auto-scroll to specific section if provided
    if (this.navParams?.data?.autoFocusElementId) {
      await this.handleAutoScrollToElement(this.navParams.data.autoFocusElementId);
    }
  }

  private async handleAutoScrollToElement(elementId: string) {
    // wait for Angular to update the view
    await waitForAngularUpdate(this.injector);
    if (this.viewDestroyed) return;

    // wait for card animations to complete (typically 300-400ms)
    await sleep(350);

    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private initializeSettingsSections() {
    this.settingsSections = [
      {
        title: LL.settings.sections.training(),
        id: 'training-section',
        requiresGeneratedPlan: true,
        requiresSubscription: true,
        items: [
          {
            icon: 'settings',
            label: LL.settings.items.trainingSettings(),
            pageName: 'ProgramSettingsPage',
            action: () => this.navigateToProgramSettings()
          },
          {
            icon: 'assignment',
            label: LL.settings.items.workouts(),
            pageName: 'EditWorkoutsPage',
            action: () => this.navigateToManageWorkouts()
          },
          {
            icon: 'exercise',
            label: LL.settings.items.editEquipment(),
            pageName: 'EquipmentSearchPage',
            action: () => this.navigateToEditEquipment()
          }
        ]
      },
      {
        title: LL.settings.sections.nutrition(),
        id: 'nutrition-section',
        requiresGeneratedPlan: true,
        requiresSubscription: true,
        items: [
          {
            icon: 'settings',
            label: LL.settings.items.nutritionSettings(),
            pageName: 'ProgramSettingsPage',
            action: () => this.navigateToNutritionSettings()
          },
          {
            icon: 'assignment',
            label: LL.settings.items.mealPlanner(),
            pageName: 'MealPlanPage',
            action: () => this.navigateToMealPlanner()
          },
          {
            icon: 'grocery',
            label: LL.settings.items.foods(),
            pageName: 'FoodsPage',
            action: () => this.navigateToFoods()
          }
        ]
      },
      {
        title: LL.settings.sections.general(),
        id: 'general-section',
        items: [
          {
            icon: 'settings',
            label: LL.settings.items.appSettings(),
            pageName: 'AppSettingsPage',
            action: () => this.navigateToAppSettings()
          },
          {
            icon: 'loyalty',
            label: LL.settings.items.manageSubscription(),
            pageName: 'UserProfilePage',
            action: () => this.navigateToManageSubscription()
          },
          {
            icon: 'notifications',
            label: LL.settings.notifications.sectionTitle(),
            pageName: 'NotificationSettingsPage',
            action: () => this.navigateToNotificationSettings(),
            isVisible: () => isCapacitorNative(),
          },
          {
            icon: 'people',
            label: LL.referral.settings.inviteRow(),
            pageName: 'ReferralHubPage',
            action: () => this.navigateToReferralHub(),
          },
          {
            icon: 'people',
            label: LL.settings.items.coaching(),
            pageName: 'CoachSettingsPage',
            action: () => this.navigateToCoachSettings(),
            isVisible: () => this.isCoachVisible(),
            isEnabled: () => true,
          },
          {
            // Privacy Choices (#4219): reopens the consent preferences dialog
            // so the user can review or change their consent answers any time
            icon: 'shield-checkmark',
            label: LL.settings.items.privacyChoices(),
            action: () => this.openPrivacyChoices()
          },
          {
            icon: 'school',
            label: LL.settings.items.admin(),
            pageName: 'AdminRootPage',
            action: () => this.navigateToAdmin(),
            isVisible: () => this.isAdminVisible()
          }
        ]
      },
      // if client has an active coach, show the coaching section
      ...(this.isCoachSectionVisible() ? [{
        title: LL.coach.settingsSection.title(),
        id: 'coaching-section',
        items: [
          {
            // Tier 1 white-labeling (#3707): show the coach's logo + brand
            // name when set, otherwise the plain person icon + coach name.
            icon: 'person-circle',
            imageUrl: this.getCoachLogoUrl(),
            label: this.getCoachBrandName() || this.getCoachName(),
          },
          {
            icon: 'chatbubbles',
            label: LL.settings.items.messageCoach(),
            pageName: 'SendCoachMessagePage',
            action: () => this.navigateToCoachMessage(),
          },
          {
            icon: 'log-out',
            label: LL.coach.settingsSection.leaveCoaching(),
            action: () => this.leaveCoachingAsync(),
          }
        ]
      }] : []),
      {
        title: LL.settings.sections.support(),
        id: 'support-section',
        items: [
          {
            icon: 'help-circle',
            label: LL.settings.items.help(),
            pageName: 'HelpPage',
            action: () => this.navigateToHelp()
          },
          {
            icon: 'outgoing-mail',
            label: LL.settings.items.feedback(),
            pageName: 'SendFeedbackPage',
            action: () => this.navigateToFeedback()
          },
          {
            icon: 'groups',
            label: LL.settings.items.community(),
            // the forum only lets subscribers in, so the row is unusable without one (#6608)
            isEnabled: () => this.hasActiveSubscription(),
            action: () => this.navigateToCommunity()
          },
          {
            icon: 'notifications-active',
            label: LL.settings.items.whatsNew(),
            pageName: 'ChangelogPage',
            action: () => this.navigateToWhatsNew()
          },
          {
            // `thumbs-up` rather than a star: there is no `star` rule in
            // cs-icon.scss, and an unknown icon name paints a solid coloured
            // square rather than erroring.
            icon: 'thumbs-up',
            label: LL.settings.items.rateTheApp(),
            action: () => this.rateTheApp()
          }
        ]
      }
    ];
  }

  // the rule itself lives in settings-section-access.ts so it can be tested
  // without Angular; the section is passed in because it is what says whether
  // this row is behind the paywall
  isItemEnabled(section: SettingsSection, item: SettingsItem): boolean {
    return isSettingsItemEnabled(section, item, {
      hasActiveSubscription: this.hasActiveSubscription(),
      welcomePhase: this.welcomePhase,
      allowedPagesInWelcome: this.allowedPagesInWelcome,
    });
  }

  private hasActiveSubscription(): boolean {
    return this.auth.getAuthRole() >= AuthRole.Paid;
  }

  isItemVisible(item: SettingsItem): boolean {
    // if custom isVisible function is provided, use it
    return item.isVisible ? item.isVisible() : true;
  }

  handleItemClick(section: SettingsSection, item: SettingsItem) {
    // only handle click if item is enabled
    if (!this.isItemEnabled(section, item)) {
      return;
    }

    if (item.action) {
      item.action();
    }
  }

  // navigation methods for Training section
  navigateToManageWorkouts() {
    void this.navCtrl.pushAsync(IEditWorkoutsPage, {});
  }

  navigateToProgramSettings() {
    void this.navCtrl.pushAsync(IProgramSettingsPage, {});
  }

  navigateToEditEquipment() {
    void this.navCtrl.pushAsync(IEquipmentSearchPage, { exercise: null });
  }

  // navigation methods for Nutrition section
  navigateToNutritionSettings() {
    void this.navCtrl.pushAsync(IProgramSettingsPage, { tab: ProgramSettingsTab.Nutrition });
  }

  navigateToMealPlanner() {
    void this.navCtrl.pushAsync(IMealPlanPage, {});
  }

  navigateToFoods() {
    void this.navCtrl.pushAsync(IFoodsPage, { mode: 'list' });
  }

  // navigation methods for Account section
  navigateToAppSettings() {
    void this.navCtrl.pushAsync(IAppSettingsPage, {});
  }

  navigateToManageSubscription() {
    void this.navCtrl.pushAsync(IUserProfilePage, {});
  }

  // navigation methods for Support section
  navigateToHelp() {
    void this.navCtrl.pushAsync(IHelpPage, {});
  }

  navigateToFeedback() {
    void this.navCtrl.pushAsync(ISendFeedbackPage, {});
  }

  navigateToCoachMessage() {
    void this.navCtrl.pushAsync(ISendCoachMessagePage, {});
  }

  // whether this person has a coach; false while the coach link cannot be read
  hasCoach(): boolean {
    return !!this.readCoachRelAsClient()?.coachUid;
  }

  // the coach link is read on every rebuild of the settings list, so a failing read is
  // reported once per page. The Coaching section is hidden while it cannot be read.
  private coachRelReadFailureReported = false;

  private readCoachRelAsClient(): CoachRelationship | undefined {
    try {
      return this.userDb.loadCoachRelAsClient();
    } catch (err) {
      if (!this.coachRelReadFailureReported) {
        this.coachRelReadFailureReported = true;
        this.logging.trackSilentProblem('SettingsCoachLinkReadFailed', { detail: describeError(err) });
      }
      return undefined;
    }
  }

  /**
   * #4625 — open the store's write-a-review screen.
   *
   * **This is a store link, not the native review prompt**, and that is what
   * makes the row allowed to exist at all: the native prompt must never be
   * attached to a button, because the operating system may show nothing and a
   * button that does nothing looks broken. A store link always works and is
   * never subject to a quota.
   *
   * **It deliberately ignores `ConfigKey.ReviewPrompts`.** That switch exists to
   * stop the app asking people unprompted; somebody who has gone to Settings and
   * tapped this has asked us. This is the one path in the feature that should
   * never depend on anything.
   */
  rateTheApp() {
    this.reviewPrompt.trackSettingsRowUsed();

    // iOS is handed off by assigning `location.href` to the `itms-apps://`
    // form, NOT through openExternalLink. That helper opens an in-app Safari
    // view on native, which renders apps.apple.com as a web page — and
    // `action=write-review` only opens the review composer once the App Store
    // APP handles the URL. `version-check.ios.ts` already does the same thing
    // for "open the store"; the scheme is what routes it, not the parameter.
    if (isPlatformIOS()) {
      window.location.href = IOS_WRITE_REVIEW_URL;
      return;
    }

    // Android and the web get the Play listing, because Play has no
    // write-review deep link at all — the listing IS where somebody rates an
    // app. Per-environment, so a dev-flavour build opens its own listing.
    void openExternalLink(androidWriteReviewUrlForEnv(AppEnvironment.name));
  }

  navigateToCommunity() {
    // use the existing method from app.component to avoid duplication
    void getMyApp().openCommunityPage();
  }

  navigateToWhatsNew() {
    void this.navCtrl.pushAsync(IChangelogPage, {});
  }

  // open the consent preferences dialog (the app-level consent-dialog
  // component listens for this event, #4219)
  openPrivacyChoices() {
    this.pubSub.publish(openConsentPreferencesEvent, undefined);
  }

  navigateToProgress() {
    void this.navCtrl.pushAsync(IProgressPage, {});
  }

  navigateToNotificationSettings() {
    void this.navCtrl.pushAsync(INotificationSettingsPage, {});
  }

  navigateToCoachSettings() {
    void this.navCtrl.pushAsync(ICoachSettingsPage, {});
  }

  isCoachVisible(): boolean {
    // hide coach settings when managing a client
    if (this.userDb.getCoachManagingClient()) return false;
    return this.auth.getIsCoach();
  }

  navigateToAdmin() {
    void this.navCtrl.pushAsync(IAdminRootPage, {});
  }

  isAdminVisible(): boolean {
    return this.auth.getAuthRole() >= AuthRole.Dev;
  }

  navigateToReferralHub() {
    void this.navCtrl.pushAsync(IReferralHubPage, {});
  }

  // intake is "pending" while the welcome flow hasn't reached Done; the rule,
  // including why the undefined welcomePhase counts as not-pending here, lives
  // in settings-section-access.ts
  isIntakePending(): boolean {
    return isIntakePendingForColumns(this.welcomePhase);
  }

  getLeftColumnSections(): SettingsSection[] {
    return splitSettingsColumns(this.settingsSections, this.welcomePhase).left;
  }

  getRightColumnSections(): SettingsSection[] {
    return splitSettingsColumns(this.settingsSections, this.welcomePhase).right;
  }

  // resume the welcome-flow intake from the Settings info card; uses
  // setRootAsync (matching intro-carousel-page) so intake completion can
  // route via the normal welcome flow rather than popping back to settings
  navigateToIntake() {
    void this.navCtrl.setRootAsync(IIntakeFormPage, {});
  }

  shouldShowInstallButton(): boolean {
    return this.installTarget.kind !== InstallTargetKind.None;
  }

  // Whether to render the "Check for updates" Settings row.
  // Mirrors the home-card render gate — hidden on iOS Phase 1
  // (stub), hidden on web where the SW isn't registered
  // (local-dev hostname or insecure context), hidden in E2E (the
  // service's isE2ETest() short-circuit so existing dashboard
  // coverage sees the layout unchanged). See
  // design-docs/in-app-update-prompt.md §Settings entry.
  shouldShowCheckForUpdatesButton(): boolean {
    return this.versionCheck.supportsUpdateChecking();
  }

  // Settings-initiated update check. Bypasses the 24h dismissal
  // window (the user explicitly asked) and bypasses the dashboard's
  // startup/resume throttle by passing surface='settings'. Two
  // paths: a real update → hand off to the backend (Play flow on
  // Android, SW skipWaiting on PWA); otherwise → "You're up to date"
  // toast.
  async checkForUpdatesAsync(): Promise<void> {
    // show a spinner for the whole check: getStatusAsync('settings') hits the
    // backend fresh (this surface bypasses the throttle) and the lookup can
    // take up to its full timeout budget — 30 s on a slow connection (iOS) or
    // a busy service worker (PWA). This is an explicit foreground tap, so it
    // must not sit silent for that long. Dismissed in `finally` so the spinner
    // never strands on the viewDestroyed early-return or on a throw.
    const loading = this.loadingCtrl.create();
    void loading.present();
    try {
      const result = await this.versionCheck.getStatusAsync('settings');
      if (this.viewDestroyed) return;
      if (result.updateAvailable) {
        // PRD §7: Settings surface emits ONLY `clicked` (one-tap
        // action, no sheet to "show" or "dismiss")
        this.telemetry.trackAppUpdateEvent('clicked', 'settings', result.availableVersion, this.versionCheck.isBelowMinimum());
        await this.versionCheck.startUpdateAsync();
      } else {
        this.toastCtrl.showToast({
          message: LL.app.updateAvailable.upToDate(),
          duration: 3000,
        });
      }
    } catch (err) {
      // surface backend failures: Phase A's Android stub throws
      // "not yet implemented"; later phases may throw on Play-Core
      // init failure or PWA SW disappeared. Per CLAUDE.md
      // "unexpected conditions must surface". Show the generic-
      // error toast so the user has feedback rather than a silent
      // no-op
      this.logging.trackAbnormalException(err);
      if (!this.viewDestroyed) {
        this.toastCtrl.showToast({
          message: LL.common.errors.generic(),
          duration: 3000,
        });
      }
    } finally {
      void loading.dismiss();
    }
  }

  installApp() {
    const target = this.installTarget;
    if (target.kind === InstallTargetKind.NativeStore || target.kind === InstallTargetKind.NativeOpen) {
      // mobile path: defer to the bottom sheet, which shows the store badge
      // and — on Android — the secondary "Open in app" link. The sheet
      // emits its own startup-sheet-impression event from ngOnInit, so here
      // we only mark the settings-button tap (cta=null, this is the intent
      // signal that the user opened the sheet via Settings).
      this.appInstallSvc.trackAppInstallEvent('settings-button', 'settings', target, null);
      void CsAppInstallSheet.present(this.popoverCtrl, target, 'settings');
    } else if (target.kind === InstallTargetKind.PwaPrompt) {
      this.appInstallSvc.trackAppInstallEvent('settings-button', 'settings', target, 'pwa-prompt');
      this.appInstallSvc.runPwaPrompt();
    }
  }

  // whether the coaching section should appear (client has a coach and is not in manage mode)
  private isCoachSectionVisible(): boolean {
    if (this.justSelfDetached) return false;
    return this.hasCoach() && !this.userDb.getCoachManagingClient();
  }

  // return the coach's display name, falling back to a generic label
  private getCoachName(): string {
    return this.readCoachRelAsClient()?.coachName || LL.coach.settingsSection.coachName();
  }

  // Tier 1 white-labeling (#3707): read the coach branding block once, guarded.
  // getSingleton can throw if the as-client singleton is not yet registered —
  // surface it (don't silently hide the brand forever) and fall back to no
  // branding. mirrors awaiting-coach.ts. Single guarded read site for both the
  // logo and the brand-name accessors below.
  private getCoachBranding(): CoachBranding | undefined {
    try {
      return this.userDb.loadCoachRelAsClient()?.branding;
    } catch (err) {
      this.logging.trackSilentProblem('SettingsPage: getCoachBranding getSingleton threw', {
        err: (err as Error)?.message || String(err),
      });
      return undefined;
    }
  }

  // the coach's logo URL, or undefined when no branding — the "Your Coach" row
  // then falls back to the person icon.
  private getCoachLogoUrl(): string | undefined {
    return this.getCoachBranding()?.logoUrl || undefined;
  }

  // the coach's brand name, or '' when unset.
  private getCoachBrandName(): string {
    return this.getCoachBranding()?.brandName || '';
  }

  // confirm and execute client-initiated disconnect from coaching
  private async leaveCoachingAsync() {
    const coachRel = this.userDb.loadCoachRelAsClient();
    const coachName = coachRel?.coachName || '';

    const action = await this.alertCtrl.showAlertAsync<'leave' | undefined>({
      title: LL.coach.selfDetach.confirmTitle(),
      message: LL.coach.selfDetach.confirmMessage({ coachName }),
      buttons: [
        { text: LL.coach.selfDetach.confirmButton(), role: AlertButtonRole.Primary, result: 'leave' },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
      ],
    });
    if (action !== 'leave') return;

    try {
      await this.cloudFunctions.coachClientSelfDetachAsync({});
    } catch (err) {
      console.error('leaveCoachingAsync: self-detach failed:', err);
      this.toastCtrl.showToast({ message: LL.common.errors.generic(), duration: 3000 });
      return;
    }

    this.toastCtrl.showToast({
      message: LL.coach.selfDetach.successToast({ coachName }),
      duration: 3000,
    });

    // hide the section immediately without waiting for CouchDB sync to delete the
    // local relationship doc — server already deleted the remote doc
    this.justSelfDetached = true;

    // re-initialize sections so the coaching section disappears
    this.initializeSettingsSections();
    this.cdr.markForCheck();
  }

  handleVersionClicked() {
    const time = new Date().getTime(); /* this is in milliseconds! */
    const delta = time - this.versionClickedTime;
    this.versionClickedTime = time;
    if (delta < 2000) {
      this.versionClickedCounter++;
    } else {
      this.versionClickedCounter = 1;
    }

    if (this.versionClickedCounter >= 5) {
      this.settings.testMode = true;
    }
  }
}